import { PointerEvent as ReactPointerEvent, ReactNode, useRef } from 'react';

import { cn } from '@/components/ui/cn';
import { GLASS } from './glass';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { Popover } from '@/components/ui/Popover';
import { Check } from '@/components/ui/icons';
import { STATUS_PALETTE } from '@/features/backlog/constants';
import { ShapeVariant } from '@/features/canvas/types';
import { MS, MS_ICON } from './ms';

/** The active canvas tool. `select` and `pan` navigate; the rest create an element on click/drag. */
export type Tool = 'select' | 'pan' | 'sticky' | 'shape' | 'text' | 'connector' | 'pen' | 'comment';

/** Pen sub-mode: a normal opaque pen, or a translucent uniform-width highlighter/marker. */
export type PenMode = 'pen' | 'highlighter';

/** Shape variant icons for the shapes flyout (Font Awesome: square / circle / triangle / diamond). */
export const SHAPE_ICON: Record<ShapeVariant, ReactNode> = {
  rectangle: <MS icon={MS_ICON.rectangle} size={19} />,
  ellipse: <MS icon={MS_ICON.ellipse} size={19} />,
  triangle: <MS icon={MS_ICON.triangle} size={19} />,
  diamond: <MS icon={MS_ICON.diamond} size={19} />,
};
const SHAPE_VARIANTS: ShapeVariant[] = ['rectangle', 'ellipse', 'triangle', 'diamond'];

/** Pen stroke widths (world px). */
export const PEN_SIZES: { size: number; label: string }[] = [
  { size: 2, label: 'Extra fine' },
  { size: 5, label: 'Fine' },
  { size: 8, label: 'Medium' },
  { size: 14, label: 'Bold' },
  { size: 24, label: 'Extra bold' },
];
const penDot = (s: number) => Math.max(4, Math.min(22, s));

/** Little corner caret marking a tool that has an options flyout (purely a hint — the whole tool button
 *  opens the flyout now, since the caret itself was too small to hit reliably on touch). */
const Caret = () => (
  <span className="pointer-events-none absolute -right-0.5 bottom-0.5 text-fg-faint">
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor"><path d="M0 2h8L4 7z" /></svg>
  </span>
);

/** Font Awesome tool glyphs, keyed to match the tool ids. */
const TOOL_ICON = (t: Exclude<Tool, 'shape'>) => <MS icon={MS_ICON[t]} size={21} />;

const TOOLS: { tool: Exclude<Tool, 'shape'>; label: string; key: string }[] = [
  { tool: 'select', label: 'Select', key: 'V' },
  { tool: 'pan', label: 'Hand (pan)', key: 'H' },
  { tool: 'sticky', label: 'Sticky note', key: 'S' },
];
const TOOLS_AFTER: { tool: Exclude<Tool, 'shape'>; label: string; key: string }[] = [
  { tool: 'text', label: 'Text', key: 'T' },
  { tool: 'connector', label: 'Connector', key: 'C' },
];

/** Tools that can be dragged from the palette straight onto the canvas. */
const PLACEABLE = new Set<Tool>(['sticky', 'shape', 'text']);

interface Props {
  tool: Tool;
  onTool: (t: Tool) => void;
  shapeVariant: ShapeVariant;
  onShapeVariant: (v: ShapeVariant) => void;
  penSize: number;
  onPenSize: (s: number) => void;
  penMode: PenMode;
  onPenMode: (m: PenMode) => void;
  penColor: string;
  markerColor: string;
  onPenColor: (c: string) => void;
  onMarkerColor: (c: string) => void;
  color: string;
  onColor: (c: string) => void;
  onPickImage: (file: File) => void;
  onPlaceStart: (tool: Tool, e: ReactPointerEvent) => void;
  inputMode: 'mouse' | 'touchpad';
  onInputMode: (m: 'mouse' | 'touchpad') => void;
  /** Board Items combo: add a backlog item-card, or a live board-search results table. */
  onAddItemCard: () => void;
  onAddSearchCard: () => void;
}

/** A card/list glyph for the "Board items" combo. */
const CardsIcon = ({ className }: { className?: string }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <rect x="3" y="4" width="13" height="16" rx="2" />
    <path d="M6.5 8h6M6.5 12h6M6.5 16h3" />
    <path d="M20 7v11a2 2 0 0 1-2 2" />
  </svg>
);
const SearchTableIcon = ({ className }: { className?: string }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <circle cx="10" cy="10" r="6" />
    <path d="m20 20-3.6-3.6" />
  </svg>
);

/** Mouse vs touchpad glyphs (outline) for the scroll-behaviour toggle. */
const MouseIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="6" y="3" width="12" height="18" rx="6" />
    <line x1="12" y1="7" x2="12" y2="11" />
  </svg>
);
const TouchpadIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <line x1="3" y1="14.5" x2="21" y2="14.5" />
  </svg>
);

const cls = (active: boolean) =>
  cn('flex h-10 w-10 items-center justify-center rounded-xl transition-colors', active ? 'bg-brand text-white' : 'text-fg-muted hover:bg-layer hover:text-fg');

/** The floating tool palette (left edge): pick a tool, drag one onto the canvas, choose a shape, set the colour, or upload an image. */
export default function Toolbar({ tool, onTool, shapeVariant, onShapeVariant, penSize, onPenSize, penMode, onPenMode, penColor, markerColor, onPenColor, onMarkerColor, color, onColor, onPickImage, onPlaceStart, inputMode, onInputMode, onAddItemCard, onAddSearchCard }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const ToolBtn = (t: { tool: Exclude<Tool, 'shape'>; label: string; key: string }) => (
    <button
      key={t.tool}
      type="button"
      title={`${t.label} (${t.key})${PLACEABLE.has(t.tool) ? ' — or drag onto the canvas' : ''}`}
      onClick={() => onTool(t.tool)}
      onPointerDown={PLACEABLE.has(t.tool) ? (e) => onPlaceStart(t.tool, e) : undefined}
      className={cls(tool === t.tool)}
    >
      {TOOL_ICON(t.tool)}
    </button>
  );

  return (
    <div
      data-toolbar
      data-wb-overlay
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(GLASS, 'pointer-events-auto absolute left-3 top-1/2 z-30 flex -translate-y-1/2 flex-col items-center gap-1 rounded-2xl p-1.5')}
    >
      {TOOLS.map(ToolBtn)}

      {/* Shape tool — tapping it selects the tool AND opens the variant flyout (the caret is just a hint) */}
      <Popover
        portal
        align="left"
        side="right"
        trigger={({ toggle }) => (
          <div className="relative flex items-center">
            <button
              type="button"
              title="Shape (R) — tap for options, or drag onto the canvas"
              onClick={() => {
                onTool('shape');
                toggle();
              }}
              onPointerDown={(e) => onPlaceStart('shape', e)}
              className={cls(tool === 'shape')}
            >
              {SHAPE_ICON[shapeVariant]}
            </button>
            <Caret />
          </div>
        )}
      >
        {(close) => (
          <div className="flex gap-1 p-1.5">
            {SHAPE_VARIANTS.map((v) => (
              <button
                key={v}
                type="button"
                title={v}
                onClick={() => {
                  onShapeVariant(v);
                  onTool('shape');
                  close();
                }}
                className={cn('flex h-9 w-9 items-center justify-center rounded-lg transition-colors', shapeVariant === v ? 'bg-brand text-white' : 'text-fg-muted hover:bg-layer hover:text-fg')}
              >
                {SHAPE_ICON[v]}
              </button>
            ))}
          </div>
        )}
      </Popover>

      {TOOLS_AFTER.map(ToolBtn)}

      {/* Board Items combo — add a backlog item-card, or a live board-search results table. */}
      <Popover
        portal
        align="left"
        side="right"
        trigger={({ toggle }) => (
          <div className="relative flex items-center">
            <button type="button" title="Board items — add an item card or a search-results table" onClick={toggle} className={cls(false)}>
              <CardsIcon />
            </button>
            <Caret />
          </div>
        )}
      >
        {(close) => (
          <div className="flex w-[236px] flex-col gap-0.5 p-1.5">
            <button type="button" onClick={() => { onAddItemCard(); close(); }} className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-layer">
              <CardsIcon className="shrink-0 text-fg-muted" />
              <span className="min-w-0"><span className="block text-[13px] font-medium text-fg-soft">Item card</span><span className="block text-[11px] text-fg-faint">Reference or create a backlog item</span></span>
            </button>
            <button type="button" onClick={() => { onAddSearchCard(); close(); }} className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-layer">
              <SearchTableIcon className="shrink-0 text-fg-muted" />
              <span className="min-w-0"><span className="block text-[13px] font-medium text-fg-soft">Search results</span><span className="block text-[11px] text-fg-faint">A live table for a board search</span></span>
            </button>
          </div>
        )}
      </Popover>

      {/* Comment tool — select it, then click the canvas to drop a pinned comment thread (Miro-style). */}
      <button
        type="button"
        title="Comment — click the canvas to add a pinned comment"
        onClick={() => onTool('comment')}
        className={cls(tool === 'comment')}
      >
        <MS icon={MS_ICON.comment} size={20} />
      </button>

      {/* Pen tool — tapping it selects the tool AND opens the flyout (pen/highlighter mode + widths) */}
      <Popover
        portal
        align="left"
        side="right"
        trigger={({ toggle }) => (
          <div className="relative flex items-center">
            <button
              type="button"
              title={penMode === 'highlighter' ? 'Highlighter (P) — tap for options' : 'Pen (P) — tap for options'}
              onClick={() => {
                onTool('pen');
                toggle();
              }}
              className={cls(tool === 'pen')}
            >
              {penMode === 'highlighter' ? <MS icon={MS_ICON.highlighter} size={21} /> : <MS icon={MS_ICON.pen} size={21} />}
            </button>
            <Caret />
          </div>
        )}
      >
        {(close) => (
          <div className="flex w-[184px] flex-col gap-1.5 p-1.5">
            <div className="flex gap-1">
              {(['pen', 'highlighter'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    onPenMode(m);
                    onTool('pen');
                  }}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[12px] font-medium transition-colors [&>svg]:h-4 [&>svg]:w-4',
                    penMode === m ? 'bg-brand text-white' : 'text-fg-muted hover:bg-layer hover:text-fg',
                  )}
                >
                  {m === 'pen' ? <MS icon={MS_ICON.pen} size={20} /> : <MS icon={MS_ICON.highlighter} size={20} />}
                  {m === 'pen' ? 'Pen' : 'Marker'}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between gap-1">
              {PEN_SIZES.map(({ size, label }) => (
                <button
                  key={size}
                  type="button"
                  title={`${label} (${size}px)`}
                  onClick={() => {
                    onPenSize(size);
                    onTool('pen');
                    close();
                  }}
                  className={cn('flex h-9 w-9 items-center justify-center rounded-lg transition-colors', penSize === size ? 'bg-brand text-white' : 'text-fg-muted hover:bg-layer hover:text-fg')}
                >
                  <span className="rounded-full bg-current" style={{ width: penDot(size), height: penDot(size), opacity: penMode === 'highlighter' ? 0.5 : 1 }} />
                </button>
              ))}
            </div>
            {/* Colour swatches for the active mode — the pen and the marker each keep their own colour. */}
            <div className="mt-0.5 grid grid-cols-6 gap-1.5 border-t border-line pt-2">
              {STATUS_PALETTE.map((c) => {
                const cur = penMode === 'highlighter' ? markerColor : penColor;
                const set = penMode === 'highlighter' ? onMarkerColor : onPenColor;
                return (
                  <button
                    key={c}
                    type="button"
                    aria-label={`${penMode === 'highlighter' ? 'Marker' : 'Pen'} colour ${c}`}
                    onClick={() => {
                      set(c);
                      onTool('pen');
                    }}
                    className="flex h-6 w-6 items-center justify-center rounded-full ring-1 ring-inset ring-black/25 transition-transform hover:scale-110"
                    style={{ background: c }}
                  >
                    {cur.toLowerCase() === c.toLowerCase() ? <Check className="text-black/80" /> : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Popover>

      <span className="my-0.5 h-px w-6 bg-line" />
      <button type="button" title="Upload image" onClick={() => fileRef.current?.click()} className="flex h-10 w-10 items-center justify-center rounded-xl text-fg-muted transition-colors hover:bg-layer hover:text-fg">
        <MS icon={MS_ICON.image} size={21} />
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPickImage(f);
          e.target.value = '';
        }}
      />
      <ColorPicker
        value={color}
        onChange={onColor}
        align="left"
        side="right"
        trigger={({ toggle }) => (
          <button type="button" title="Colour" onClick={toggle} className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl hover:bg-layer">
            <span className="h-6 w-6 rounded-full ring-1 ring-inset ring-black/25" style={{ background: color }} />
          </button>
        )}
      />

      <span className="my-0.5 h-px w-6 bg-line" />
      {/* Scroll-behaviour toggle: mouse (scroll = zoom) vs touchpad (scroll = pan). Icon shows the current mode. */}
      <button
        type="button"
        title={inputMode === 'mouse' ? 'Mouse mode — scroll zooms (click for touchpad: scroll pans)' : 'Touchpad mode — scroll pans (click for mouse: scroll zooms)'}
        onClick={() => onInputMode(inputMode === 'mouse' ? 'touchpad' : 'mouse')}
        className="flex h-10 w-10 items-center justify-center rounded-xl text-fg-muted transition-colors hover:bg-layer hover:text-fg"
      >
        {inputMode === 'mouse' ? <MouseIcon /> : <TouchpadIcon />}
      </button>
    </div>
  );
}
